import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators, AbstractControl } from '@angular/forms';
import { emailValidator } from 'util/form.utility';
import { NgbCollapseModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BreadcrumbsComponent } from 'components/breadcrumbs/breadcrumbs.component';
import { ContentSideNavComponent } from 'components/content-side-nav/content-side-nav.component';
import { MultiOptionButtonComponent } from 'components/multi-option-button/multi-option-button.component';
import { DatePickerComponent, DateRangeMode } from 'components/date-picker/date-picker.component';
import { FileUploadComponent } from 'components/file-upload/file-upload.component';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { SpinnerComponent } from 'components/spinner/spinner.component';
import { IrConversationComponent } from 'components/service/ir-conversation/ir-conversation.component';
import { IrInvoiceComponent } from 'components/service/ir-invoice/ir-invoice.component';
import { IrTasksComponent } from 'components/service/ir-tasks/ir-tasks.component';
import { IrProcessHistoryComponent } from 'components/service/ir-process-history/ir-process-history.component';
import { ConfirmModalComponent } from 'components/modal/confirm-modal/confirm-modal.component';
import { IrRequestInfoModalComponent } from 'components/modal/ir-request-info-modal/ir-request-info-modal.component';
import { SelectStructuralUnitModalComponent } from 'components/modal/select-structural-unit-modal/select-structural-unit-modal.component';
import { SearchUserInputComponent } from 'components/search-user-input/search-user-input.component';
import { InfoRequestFormComponent } from 'components/service/info-request/info-request-form/info-request-form.component';
import { InfoRequestClientPayerFormComponent } from 'components/service/info-request/info-request-client-payer-form/info-request-client-payer-form.component';
import { ApiService } from 'services/api.service';
import { AuthorizationService } from 'services/authorization.service';
import { ToastService } from 'services/toast.service';
import { IrFormLabelsService } from 'services/ir-form-labels.service';
import { Breadcrumb } from 'types/breadcrumb.interface';
import {
  InformationRequestCard,
  InformationRequestBackofficeStatus,
  InformationRequestTopicResponse,
  UpdateBoInformationRequestRequest,
  CompleteInformationRequestRequest,
  CancelBoInformationRequestRequest,
  ResponseDraftItem,
  SendingEventItem,
  SendingEventParentType,
  RecordSendingEventRequest,
  NamejsDocumentKind,
  FILE_OBJECT_TYPE_INFO_REQUEST_DOCUMENT,
  FILE_OBJECT_TYPE_INFO_REQUEST_AUTHORIZATION,
  FILE_OBJECT_TYPE_INFO_REQUEST_DISCOUNT,
  FILE_OBJECT_TYPE_INFO_REQUEST_OTHER_RESPONSE,
  FILE_OBJECT_TYPE_INFO_REQUEST_RESPONSE_DRAFT,
  FILE_OBJECT_TYPE_INFO_REQUEST_NOTE,
  ForwardInformationRequestRequest,
  HandOverInformationRequestRequest,
  InformationRequestCalculationListByInformationRequestIdItem,
  InformationRequestInvoiceListByInformationRequestIdItem,
  InformationRequestDocumentItem,
  InformationRequestDocumentFile,
  FILE_OBJECT_TYPE_INFO_REQUEST_BO_DOCUMENT,
  FILE_OBJECT_TYPE_INFO_REQUEST_CALCULATION,
  PrintBlock,
  PrintDocument,
  PrintLine,
  PrintSection
} from 'types/information-request.interface';
import type {
  BackofficeInformationRequestCommunicationListItem,
  BackofficeInformationRequestCommunicationCard,
  BackofficeInformationRequestCommunicationListItemPagedResponse,
  InformationRequestNoteListByInformationRequestIdItem,
  InformationRequestTaskCard,
  InformationRequestTaskListItem,
  InformationRequestTaskListResult,
  BackofficeInformationRequestCard
} from 'types/information-request.interface';
import { ClientCard } from 'types/client.interface';
import { ClassifierValueCard } from 'types/classifier.interface';
import { FileCard } from 'types/file.interface';
import { LegalEntityFormOption } from 'types/legal-entity.interface';
import { StructuralUnitMinimalListItem } from 'types/structural-unit.interface';
import { CLAIM_SCOPES, CLAIM_TYPES } from 'types/user.interface';
import { formatDatetime } from 'util/date.utility';
import { getStructuralUnitsForClaim } from 'util/user.utility';
import { NgxDropzoneModule, NgxDropzoneChangeEvent } from 'ngx-dropzone';
import { Observable, Subscription, finalize, forkJoin } from 'rxjs';
import {
  runUploads,
  isolateUploads,
  countUploadFailures,
  isolateFileActions,
  countFileActionFailures,
  firstFileActionError
} from 'util/file-upload.utility';

const PRELIMINARY_RESEARCH_STATUSES: InformationRequestBackofficeStatus[] = ['NewPortal', 'NewBackoffice', 'NewRedirected'];
const START_PROCESSING_STATUSES: InformationRequestBackofficeStatus[] = [
  'PreliminaryResearch',
  'AdditionalInfoRequested',
  'AdditionalInfoSubmitted',
  'Completed',
  'Cancelled'
];
const COMPLETE_CANCEL_EXCLUDED_STATUSES: InformationRequestBackofficeStatus[] = [
  'NewPortal',
  'NewBackoffice',
  'NewRedirected',
  'Completed',
  'Cancelled'
];
const REDIRECT_EXCLUDED_STATUSES: InformationRequestBackofficeStatus[] = ['Draft', 'AdditionalInfoRequested'];
const REASSIGN_EXCLUDED_STATUSES: InformationRequestBackofficeStatus[] = ['Draft', 'Completed', 'Cancelled'];
const REQUEST_ADDITIONAL_INFO_EXCLUDED_STATUSES: InformationRequestBackofficeStatus[] = [
  'NewPortal',
  'NewBackoffice',
  'NewRedirected',
  'Completed',
  'Cancelled'
];
// LNA-1929: terminal statuses where a deleted client blocks any further handling.
const CLIENT_DELETED_RESTRICTED_STATUSES: InformationRequestBackofficeStatus[] = ['Completed', 'Cancelled'];

const RESPONSE_TAB_READONLY_STATUSES: InformationRequestBackofficeStatus[] = [
  'Draft',
  'NewPortal',
  'NewBackoffice',
  'NewRedirected',
  'Completed',
  'Cancelled'
];

// LNAEP-940: response document types are now an editable classifier (Admin section) instead of a hardcoded enum.
const RESPONSE_DOCUMENT_TYPE_CLASSIFIER_CODE = 'AtbildesDokumentaVeids';
// The "Dokumenta kopija" value carries extra logic (the "Skaits"/count field) and cannot be removed/edited in Admin.
const DOCUMENT_COPY_VALUE_CODE = 'AtbildesDokumentaVeids.DocumentCopy';

const KEY_ACTION_REDIRECT = 'pages.viewInfoRequest.actions.redirect';
const KEY_ACTION_REASSIGN = 'pages.viewInfoRequest.actions.reassign';
const KEY_ACTION_CANCEL = 'pages.viewInfoRequest.actions.annul';
const KEY_ACTION_REQUEST_ADDITIONAL_INFO = 'pages.viewInfoRequest.actions.requestAdditionalInfo';
const KEY_ACTION_START_PROCESSING = 'pages.viewInfoRequest.actions.startProcessing';
const MAX_NOTE_LENGTH = 255;
const MAX_OTHER_RESPONSE_DESCRIPTION_LENGTH = 255;
const MAX_BODY_LENGTH = 2000;

type PrintGrid = 'basic' | 'two-col';

type PrintItem =
  | { kind: 'fields'; lines: PrintLine[]; grid: PrintGrid }
  | { kind: 'card'; items: PrintItem[] }
  | { kind: 'heading'; value: string; tooltipKey: string | null; boxed: boolean }
  | { kind: 'text'; value: string }
  | { kind: 'paragraph'; value: string }
  | { kind: 'consent'; value: string }
  | { kind: 'table'; line: PrintLine }
  | { kind: 'documents'; line: PrintLine }
  | { kind: 'banner'; value: string; tooltipKey: string | null };

@Component({
  selector: 'app-view-info-request-page',
  standalone: true,
  imports: [
    TranslateModule,
    SpinnerComponent,
    ContentSideNavComponent,
    BreadcrumbsComponent,
    MultiOptionButtonComponent,
    DatePickerComponent,
    FileUploadComponent,
    TooltipComponent,
    ReactiveFormsModule,
    FormsModule,
    NgClass,
    NgTemplateOutlet,
    NgbCollapseModule,
    RouterLink,
    InfoRequestFormComponent,
    InfoRequestClientPayerFormComponent,
    NgxDropzoneModule,
    IrConversationComponent,
    IrInvoiceComponent,
    IrTasksComponent,
    SearchUserInputComponent,
    ConfirmModalComponent,
    IrRequestInfoModalComponent,
    IrProcessHistoryComponent,
    SelectStructuralUnitModalComponent
  ],
  templateUrl: './view-info-request-page.component.html',
  styleUrls: ['./view-info-request-page.component.scss', '../../components/form/base-form.scss']
})
export class ViewInfoRequestPageComponent implements OnInit {
  public readonly formatDatetime = formatDatetime;

  // LNA-1927: IR form section/field captions are owned by the backend; expose the lookup to the template.
  get labels(): IrFormLabelsService {
    return this.irFormLabels;
  }

  breadcrumbs: Breadcrumb[] = [{ title: 'components.breadcrumbs.infoRequestWorkspace', url: '/informacijas-pieprasijums' }];
  requestId: string = '';
  request?: InformationRequestCard;
  isLoading: boolean = true;

  /**
   * Tracks the in-flight loadData forkJoin so we can cancel it when the user
   * fires another tab switch before the previous load completes. Without this,
   * rapid tab clicks let the older slower-resolving forkJoin overwrite the
   * fresher response (last-resolves-wins race), surfacing stale data on the UI.
   */
  private loadDataSubscription?: Subscription;

  readonly navItems: string[] = [
    'infoRequestApplication',
    'infoRequestCommunication',
    'infoRequestInvoice',
    'infoRequestResponse',
    'infoRequestTasks',
    'infoRequestProcessNotes',
    'infoRequestProcessHistory',
    'infoRequestDocuments'
  ];
  activeItem: string = this.navItems[0];
  navItemsCounts: { [key: string]: number } = {};
  hasUnreadMessages: boolean = false;
  hasActiveTasks: boolean = false;
  messageCount: number = 0;
  unreadMessageCount: number = 0;
  conversationList?: BackofficeInformationRequestCommunicationListItemPagedResponse;
  notes?: InformationRequestNoteListByInformationRequestIdItem[];
  notesCount: number = 0;
  isSubmittingNoteFiles: boolean = false;
  readonly fileTypeSubmissionNote = FILE_OBJECT_TYPE_INFO_REQUEST_NOTE;
  isAddingNote: boolean = false;
  newNoteText: string = '';
  newNoteId: string = '';
  isSubmittingNewNoteFiles: boolean = false;
  editingNoteId: string | null = null;
  editingNoteText: string = '';
  private editingNoteFilesChanged = false;
  isConfirmDeleteNoteOpen: boolean = false;
  noteToDeleteId: string | null = null;

  taskList: InformationRequestTaskListItem[] = [];
  taskCount: number = 0;
  minTaskExecutionOrder: number = 1;

  invoiceTabCount: number = 0;
  irCalculations: InformationRequestCalculationListByInformationRequestIdItem[] = [];
  irInvoices: InformationRequestInvoiceListByInformationRequestIdItem[] = [];

  parsedRequesterData?: Record<string, string>;
  parsedPayerData?: Record<string, string>;
  parsedDiscountData?: Record<string, string>;
  isOrganization: boolean = false;

  isBasicInfoExpanded: boolean = true;
  isRequestExpanded: boolean = true;
  isDocumentsExpanded: boolean = true;
  isClientPayerExpanded: boolean = true;

  readonly fileObjectType = FILE_OBJECT_TYPE_INFO_REQUEST_DOCUMENT;
  readonly fileTypeAuthorization = FILE_OBJECT_TYPE_INFO_REQUEST_AUTHORIZATION;
  readonly fileTypeOtherResponse = FILE_OBJECT_TYPE_INFO_REQUEST_OTHER_RESPONSE;
  readonly fileTypeResponseDraft = FILE_OBJECT_TYPE_INFO_REQUEST_RESPONSE_DRAFT;

  // Response tab
  documentTypeOptions: ClassifierValueCard[] = [];
  isDocumentTypeEditMode: boolean = false;
  documentTypeRows: { valueId: string; count: number | null }[] = [];
  @ViewChild('addOtherResponseModal') addOtherResponseModalRef!: TemplateRef<any>;
  @ViewChild('addDraftModal') addDraftModalRef!: TemplateRef<any>;
  @ViewChild('sendEmailModal') sendEmailModalRef!: TemplateRef<any>;
  @ViewChild('sendPortalModal') sendPortalModalRef!: TemplateRef<any>;
  @ViewChild('markSentModal') markSentModalRef!: TemplateRef<any>;
  addOtherResponseForm!: FormGroup;
  sendEmailForm!: FormGroup;
  sendPortalForm!: FormGroup;
  markSentForm!: FormGroup;
  sendingEventParentType: SendingEventParentType | null = null;
  sendingEventParentId: string | null = null;
  sendEmailExistingFiles: FileCard[] = [];
  sendPortalExistingFiles: FileCard[] = [];
  editingDraftId: string | null = null;
  editingDraftText: string = '';
  editDraftTouched: boolean = false;
  isSubmittingDraftFiles: boolean = false;
  isAddingDraft: boolean = false;
  newDraftText: string = '';
  draftTouched: boolean = false;
  newDraftFiles: File[] = [];
  newDraftId: string = '';
  isSubmittingNewDraftFiles: boolean = false;
  isSubmittingOtherResponseFiles: boolean = false;
  editingOtherResponseId: string | null = null;
  editingOtherResponseDescription: string = '';
  editOtherResponseTouched: boolean = false;
  newOtherResponseFiles: File[] = [];
  isAddingOtherResponse: boolean = false;
  newOtherResponseDescription: string = '';
  otherResponseTouched: boolean = false;
  newOtherResponseId: string = '';
  isSubmittingNewOtherResponseFiles: boolean = false;

  // Documents tab
  readonly fileTypeBoDocument = FILE_OBJECT_TYPE_INFO_REQUEST_BO_DOCUMENT;
  irDocuments: InformationRequestDocumentItem[] = [];
  irDocumentsCount: number = 0;
  isAddingDocument: boolean = false;
  newDocumentDescription: string = '';
  newDocumentTouched: boolean = false;
  newDocumentFile: File | null = null;
  editingDocumentOuterId: string | null = null;
  editingDocumentDescription: string = '';
  editDocumentTouched: boolean = false;
  isConfirmDeleteDocumentOpen: boolean = false;
  documentToDeleteOuterId: string | null = null;
  readonly MAX_DOCUMENT_DESCRIPTION_LENGTH = 1000;
  editingDocumentMaxLength: number = this.MAX_DOCUMENT_DESCRIPTION_LENGTH;

  get documentFiles() {
    return (this.request?.documents || []).filter((f) => f.objectType === this.fileObjectType);
  }

  get authorizationFiles() {
    return (this.request?.documents || []).filter((f) => f.objectType === this.fileTypeAuthorization);
  }

  getFilesForEntity(type: string, entityId: string) {
    return (this.request?.documents || []).filter((f) => f.objectType === type && f.objectId === entityId);
  }

  structuralUnits: StructuralUnitMinimalListItem[] = [];
  legalEntityFormOptions: LegalEntityFormOption[] = [];

  // Action buttons
  readonly DateRangeMode = DateRangeMode;
  readonly MAX_NOTE_LENGTH = MAX_NOTE_LENGTH;
  readonly MAX_BODY_LENGTH = MAX_BODY_LENGTH;
  isActionLoading: boolean = false;
  isRequestAdditionalInfoModalOpen: boolean = false;
  startProcessingForm!: FormGroup;
  completeForm!: FormGroup;
  cancelForm!: FormGroup;
  @ViewChild('topicFormEdit') topicFormEdit?: InfoRequestFormComponent;
  @ViewChild(IrInvoiceComponent) irInvoiceTab?: IrInvoiceComponent;
  @ViewChild('requestDocumentUpload') requestDocumentUpload?: FileUploadComponent;
  @ViewChild('editOtherResponseFileUpload') editOtherResponseFileUpload?: FileUploadComponent;
  @ViewChild('editNoteFileUpload') editNoteFileUpload?: FileUploadComponent;
  @ViewChild('clientPayerFormEdit') clientPayerFormEdit?: InfoRequestClientPayerFormComponent;
  @ViewChild('startProcessingModal') startProcessingModalRef!: TemplateRef<any>;
  @ViewChild('completeModal') completeModalRef!: TemplateRef<any>;
  @ViewChild('cancelModal') cancelModalRef!: TemplateRef<any>;
  @ViewChild('forwardModal') forwardModalRef!: TemplateRef<any>;
  @ViewChild('handOverModal') handOverModalRef!: TemplateRef<any>;
  forwardForm!: FormGroup;
  handOverForm!: FormGroup;
  isSelectStructuralUnitModalOpen: boolean = false;
  userStructuralUnits: { id: string; name: string }[] = [];

  // Edit modes
  isBasicInfoEditMode: boolean = false;
  isRequestEditMode: boolean = false;
  private isRequestDocumentsSaving: boolean = false;
  isClientPayerEditMode: boolean = false;
  isUnsavedChangesModalOpen: boolean = false;
  pendingNavItem: string = '';
  basicInfoForm!: FormGroup;
  topics: InformationRequestTopicResponse[] = [];
  client?: ClientCard;

  get currentUserId(): string {
    return this.authService.userSecurityInfo()?.userId || '';
  }

  get isTaskCreationDisabled(): boolean {
    if (!this.request) return true;
    return PRELIMINARY_RESEARCH_STATUSES.includes(this.request.backofficeStatus);
  }

  get showStartPreliminaryButton(): boolean {
    if (!this.request) return false;
    return PRELIMINARY_RESEARCH_STATUSES.includes(this.request.backofficeStatus);
  }

  get showStartProcessingOption(): boolean {
    if (!this.request) return false;
    return START_PROCESSING_STATUSES.includes(this.request.backofficeStatus) && this.isUserInResponsibleUnit();
  }

  get showCompleteButton(): boolean {
    if (!this.request) return false;
    return !COMPLETE_CANCEL_EXCLUDED_STATUSES.includes(this.request.backofficeStatus);
  }

  get showAnnulOption(): boolean {
    if (!this.request) return false;
    return !COMPLETE_CANCEL_EXCLUDED_STATUSES.includes(this.request.backofficeStatus);
  }

  get showRedirectOption(): boolean {
    if (!this.request) return false;
    return !REDIRECT_EXCLUDED_STATUSES.includes(this.request.backofficeStatus);
  }

  get showReassignOption(): boolean {
    if (!this.request) return false;
    return !REASSIGN_EXCLUDED_STATUSES.includes(this.request.backofficeStatus);
  }

  get isClientDeleted(): boolean {
    return this.client?.isDeleted === true;
  }

  // LNA-1929: once the client is deleted, a Completed/Cancelled request must not allow further
  // handling — Forward to another archive, Hand over to another employee, Start processing.
  get areDeletedClientActionsRestricted(): boolean {
    if (!this.request) return false;
    return this.isClientDeleted && CLIENT_DELETED_RESTRICTED_STATUSES.includes(this.request.backofficeStatus);
  }

  get otherActionsOptions(): string[] {
    const options: string[] = [];
    const deletedClientRestricted = this.areDeletedClientActionsRestricted;
    if (this.showRedirectOption && !deletedClientRestricted) {
      options.push(KEY_ACTION_REDIRECT);
    }
    if (this.showReassignOption && !deletedClientRestricted) {
      options.push(KEY_ACTION_REASSIGN);
    }
    if (this.showAnnulOption) {
      options.push(KEY_ACTION_CANCEL);
    }
    if (this.request && !REQUEST_ADDITIONAL_INFO_EXCLUDED_STATUSES.includes(this.request.backofficeStatus)) {
      options.push(KEY_ACTION_REQUEST_ADDITIONAL_INFO);
    }
    if (this.showStartProcessingOption && !deletedClientRestricted) {
      options.push(KEY_ACTION_START_PROCESSING);
    }
    return options;
  }

  constructor(
    private apiService: ApiService,
    private route: ActivatedRoute,
    private router: Router,
    private translateService: TranslateService,
    private authService: AuthorizationService,
    private toastService: ToastService,
    private fb: FormBuilder,
    public modalService: NgbModal,
    private irFormLabels: IrFormLabelsService
  ) {
    this.startProcessingForm = this.fb.group({
      deadlineDate: ['', Validators.required]
    });
    this.completeForm = this.fb.group({
      note: ['', Validators.maxLength(MAX_NOTE_LENGTH)]
    });
    this.cancelForm = this.fb.group({
      note: ['', [Validators.required, Validators.maxLength(MAX_NOTE_LENGTH)]]
    });
    this.basicInfoForm = this.fb.group({
      submissionChannel: ['', Validators.required],
      serviceDeadline: ['', Validators.required],
      deadlineDate: [''],
      mustCompleteByDate: [''],
      letterNumber: ['', Validators.maxLength(50)],
      aboutSubject: ['', Validators.maxLength(50)],
      requiresRestrictedAccess: [false]
    });
    this.addOtherResponseForm = this.fb.group({
      description: ['', Validators.required]
    });
    this.sendEmailForm = this.fb.group({
      addressDetail: ['', Validators.required],
      emailSubject: ['', Validators.required],
      emailBody: ['', [Validators.required, Validators.maxLength(MAX_BODY_LENGTH)]]
    });
    this.sendPortalForm = this.fb.group({
      clientMessage: ['', Validators.maxLength(MAX_NOTE_LENGTH)]
    });
    this.markSentForm = this.fb.group({
      actualDeliveryMethod: ['', Validators.required],
      addressDetail: [''],
      emailSubject: [''],
      emailBody: [''],
      backofficeNote: ['', Validators.maxLength(MAX_NOTE_LENGTH)]
    });
    this.markSentForm.get('actualDeliveryMethod')?.valueChanges.subscribe((method: string) => {
      const addressControl = this.markSentForm.get('addressDetail') as AbstractControl;
      if (method === 'Email') {
        addressControl.setValidators([Validators.required, emailValidator()]);
      } else {
        addressControl.clearValidators();
      }
      addressControl.setValue(this.resolveResponseAddressDefault(method));
      addressControl.updateValueAndValidity();
    });
    this.forwardForm = this.fb.group({
      targetStructuralUnitId: ['', Validators.required],
      justification: ['', Validators.maxLength(MAX_NOTE_LENGTH)]
    });
    this.handOverForm = this.fb.group({
      targetUserId: ['', Validators.required],
      justification: ['', [Validators.required, Validators.maxLength(MAX_NOTE_LENGTH)]]
    });
  }

  ngOnInit(): void {
    window.scrollTo(0, 0);
    this.openRequestedTab();
    this.irFormLabels.ensureLoaded();
    this.apiService.getStructuralUnitOptions().subscribe({
      next: (data) => {
        this.structuralUnits = data;
        this.enrichStructuralUnitNames();
      },
      error: (err) => console.error(err)
    });
    this.apiService.getLegalEntityForms().subscribe({
      next: (options) => {
        this.legalEntityFormOptions = options;
      },
      error: (err) => console.error(err)
    });
    this.apiService.getInformationRequestTopics().subscribe({
      next: (data) => {
        this.topics = data;
      },
      error: (err) => console.error(err)
    });
    this.apiService.getClassifierValues(RESPONSE_DOCUMENT_TYPE_CLASSIFIER_CODE).subscribe({
      next: (card) => {
        this.documentTypeOptions = (card.values || [])
          .filter((v) => !v.isDeleted)
          .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'lv'));
      },
      error: (err) => console.error('Failed to load response document type classifier:', err)
    });
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id') || '';
      if (id.length > 0) {
        this.requestId = id;
        this.loadData();
      }
    });
  }

  /**
   * LNA-2050 #13. A link can name the tab to open: task e-mails and notifications link the task
   * number to `?activeItem=infoRequestTasks` (spec sheet "LNA" r30). An unknown value keeps the first tab.
   */
  openRequestedTab(): void {
    const requested = this.route.snapshot.queryParamMap.get('activeItem');
    if (requested && this.navItems.includes(requested)) {
      this.activeItem = requested;
    }
  }

  getStructuralUnitName(id?: string | null): string {
    if (!id) return '-';
    return this.structuralUnits.find((u) => u.id === id)?.name || '-';
  }

  private enrichStructuralUnitNames(): void {
    if (!this.request || this.structuralUnits.length === 0) return;
    const find = (id?: string | null) => (id ? (this.structuralUnits.find((u) => u.id === id)?.name ?? null) : null);
    this.request.invoiceDeliveryStructuralUnitName = find(this.request.invoiceDeliveryStructuralUnitId);
    this.request.responseDeliveryStructuralUnitName = find(this.request.responseDeliveryStructuralUnitId);
  }

  /** `onSettled` runs once this load is over, whether it landed, failed or was cancelled by a newer one. */
  loadData(onSettled?: () => void): void {
    // Cancel any previous in-flight load — rapid tab switches would otherwise
    // race and let the older response overwrite the newer one on resolution.
    this.loadDataSubscription?.unsubscribe();
    this.loadDataSubscription = forkJoin([
      this.apiService.getInformationRequest(this.requestId),
      this.apiService.getIrCommunicationList(this.requestId),
      this.apiService.getIrNotesByRequestId(this.requestId),
      this.apiService.getIrTaskList(this.requestId),
      this.apiService.getIrCalculationsByRequestId(this.requestId),
      this.apiService.getIrInvoicesByRequestId(this.requestId),
      this.apiService.getIrDocuments(this.requestId)
    ]).pipe(finalize(() => onSettled?.())).subscribe({
      next: ([data, conversation, notes, taskResult, calculations, invoices, documents]) => {
        this.request = data;
        this.enrichStructuralUnitNames();
        this.isOrganization = !!data.legalEntity;
        this.parseJsonData();
        this.setBreadcrumbs();

        this.conversationList = conversation;
        this.notes = notes;
        this.messageCount = this.conversationList?.totalCount ?? 0;
        this.unreadMessageCount = this.conversationList?.list.filter((msg) => !msg.isReadByBackoffice && msg.frontofficeText).length ?? 0;
        this.hasUnreadMessages = this.unreadMessageCount > 0;
        this.notesCount = this.notes.length;
        this.taskList = taskResult.page.list;
        this.taskCount = this.countAcceptableTasks(this.taskList);
        this.irCalculations = calculations;
        this.irInvoices = invoices;
        this.invoiceTabCount = calculations.length + invoices.length;
        this.irDocuments = documents;
        this.irDocumentsCount = this.irDocuments.length;
        this.computeMinTaskOrder();
        this.updateHasActiveTasks();
        this.navItemsCounts = {
          infoRequestCommunication: this.messageCount,
          infoRequestProcessNotes: this.notesCount,
          infoRequestTasks: this.taskCount,
          infoRequestInvoice: this.invoiceTabCount,
          infoRequestDocuments: this.irDocumentsCount
        };

        this.isLoading = false;
        if (data.clientId) {
          this.apiService.getClient(data.clientId).subscribe({
            next: (client) => {
              this.client = client;
            },
            error: (err) => console.error(err)
          });
        }
      },
      error: (error) => {
        console.error(error);
        this.isLoading = false;
      }
    });
  }

  /**
   * Refresh the IR (status, response sections, counts, etc.) on every tab switch so
   * background events that mutated the IR — Namejs polling reaching terminal status,
   * auto-fetch landing a .edoc, registrar registering the doc, etc. — surface without
   * forcing the operator to hard-reload. Skips the reload when the user re-selects
   * the same tab.
   */
  onNavItemSelected(item: string): void {
    if (this.activeItem === item) return;
    if (this.hasUnsavedChanges()) {
      this.pendingNavItem = item;
      this.isUnsavedChangesModalOpen = true;
      return;
    }
    this.activeItem = item;
    this.loadData();
  }

  private hasUnsavedChanges(): boolean {
    return (
      this.isBasicInfoEditMode ||
      this.isRequestEditMode ||
      this.isClientPayerEditMode ||
      this.hasInvoiceTabChanges ||
      this.hasResponseTabChanges ||
      this.hasNotesTabChanges ||
      this.hasDocumentsTabChanges
    );
  }

  private get hasInvoiceTabChanges(): boolean {
    return this.irInvoiceTab?.hasUnsavedChanges ?? false;
  }

  private get hasResponseTabChanges(): boolean {
    return (
      this.isDocumentTypeEditMode ||
      this.isAddingOtherResponse ||
      this.editingOtherResponseId !== null ||
      this.isAddingDraft ||
      this.editingDraftId !== null
    );
  }

  private get hasNotesTabChanges(): boolean {
    return this.isAddingNote || this.editingNoteId !== null;
  }

  private get hasDocumentsTabChanges(): boolean {
    return this.isAddingDocument || this.editingDocumentOuterId !== null;
  }

  confirmDiscardChanges(): void {
    this.isUnsavedChangesModalOpen = false;
    this.isBasicInfoEditMode = false;
    this.isRequestEditMode = false;
    this.isClientPayerEditMode = false;
    this.cancelDocumentTypeEdit();
    this.cancelAddOtherResponse();
    this.resetEditOtherResponse();
    this.cancelAddDraft();
    this.resetEditDraft();
    this.cancelAddNote();
    this.cancelEditNote();
    this.cancelAddDocument();
    this.cancelEditDocument();
    this.activeItem = this.pendingNavItem;
    this.loadData();
  }

  cancelDiscardChanges(): void {
    this.isUnsavedChangesModalOpen = false;
    this.pendingNavItem = '';
  }

  get selectedTopic(): InformationRequestTopicResponse | undefined {
    if (!this.request?.topicType) return undefined;
    return this.topics.find((t) => t.topicType === this.request!.topicType);
  }

  getHeadingTitle(): string {
    if (!this.request) return '';
    const parts: string[] = [];
    const prefix = this.translateService.instant('pages.viewInfoRequest.titlePrefix');
    if (this.request.code) {
      parts.push(`${prefix} ${this.request.code}`);
    }
    if (this.isOrganization && this.request.legalEntity?.name) {
      const formName = this.request.legalEntity?.type ? this.getLegalEntityFormName(this.request.legalEntity.type) : '';
      const clientDisplay =
        formName && formName !== this.request.legalEntity.type
          ? `${formName} "${this.request.legalEntity.name}"`
          : this.request.legalEntity.name;
      parts.push(clientDisplay);
    } else if (this.request.client?.name || this.request.client?.surname) {
      parts.push(`${this.request.client?.name || ''} ${this.request.client?.surname || ''}`.trim());
    }
    if (this.request.topicVersion?.name) {
      parts.push(this.request.topicVersion.name);
    }
    if (this.request.aboutSubject) {
      const truncated =
        this.request.aboutSubject.length > 60 ? `(${this.request.aboutSubject.substring(0, 60)}...)` : `(${this.request.aboutSubject})`;
      parts.push(truncated);
    }
    return parts.join(', ');
  }

  private setBreadcrumbs(): void {
    const code = this.request?.code
      ? `${this.translateService.instant('pages.viewInfoRequest.titlePrefix')} ${this.request.code}`
      : this.requestId;
    this.breadcrumbs = [
      { title: 'components.breadcrumbs.infoRequestWorkspace', url: '/informacijas-pieprasijums' },
      { title: code, url: `/informacijas-pieprasijums/${this.requestId}` }
    ];
  }

  private parseJsonData(): void {
    this.parsedRequesterData = undefined;
    this.parsedPayerData = undefined;
    this.parsedDiscountData = undefined;

    if (this.request?.requesterForOtherPersonData) {
      try {
        this.parsedRequesterData = JSON.parse(this.request.requesterForOtherPersonData);
      } catch {
        /* empty */
      }
    }
    if (this.request?.payerData) {
      try {
        this.parsedPayerData = JSON.parse(this.request.payerData);
      } catch {
        /* empty */
      }
    }
    if (this.request?.discountGroupData) {
      try {
        const parsed = JSON.parse(this.request.discountGroupData);
        if (!parsed?.none) {
          this.parsedDiscountData = parsed;
        }
      } catch {
        /* empty */
      }
    }
  }

  getServiceTypeLabel(): string {
    return this.translateService.instant(`pages.service.infoRequest.serviceType.${this.request?.serviceType}`);
  }

  getTopicName(): string {
    return this.request?.topicVersion?.name || '-';
  }

  getTopicChipLabel(): string {
    const name = this.request?.topicVersion?.name;
    if (!name) return '-';
    return `${this.irFormLabels.field('requestContent', 'selectedTopic')} - ${name}`;
  }

  getSubmissionChannelLabel(): string {
    return this.irFormLabels.enumValue('submissionChannel', this.request?.submissionChannel);
  }

  getDeadlineLabel(): string {
    return this.irFormLabels.enumValue('serviceDeadline', this.request?.serviceDeadline);
  }

  get paymentMarker(): string | null {
    const state = this.request?.paymentState;
    return state && state !== 'None' ? state : null;
  }

  getBackofficeStatusLabel(): string {
    return this.translateService.instant(`pages.service.infoRequest.backofficeStatus.${this.request?.backofficeStatus}`);
  }

  getRequesterRelationLabel(): string {
    const type = this.request?.requesterRelationType;
    if (type === 'Unknown' && this.request?.requesterForOtherPersonType) {
      return this.irFormLabels.enumValue('requesterRelation', 'ForOtherPerson');
    }
    if (type === 'Unknown') return '-';
    return this.irFormLabels.enumValue('requesterRelation', type);
  }

  getPayerTypeLabel(): string {
    return this.request?.payerType === 'Unknown' ? '-' : this.irFormLabels.enumValue('payerType', this.request?.payerType);
  }

  get hasJpAuthorizationData(): boolean {
    if (this.request?.requesterForOtherPersonType === 'AuthorizationOrPermission') return true;
    if (this.authorizationFiles.length > 0) return true;
    if (!this.parsedRequesterData) return false;
    return !!(
      this.parsedRequesterData['name'] ||
      this.parsedRequesterData['surname'] ||
      this.parsedRequesterData['personalCode'] ||
      this.parsedRequesterData['authorizationNumber']
    );
  }

  get hasDiscount(): boolean {
    return !!this.request?.discountGroupType || this.isDiscountNone;
  }

  get isDiscountNone(): boolean {
    if (!this.request?.discountGroupData) return false;
    try {
      return JSON.parse(this.request.discountGroupData)?.none === true;
    } catch {
      return false;
    }
  }

  getDiscountLabel(): string {
    if (this.isDiscountNone) {
      return this.irFormLabels.enumValue('discountGroup', 'None');
    }
    return this.irFormLabels.enumValue('discountGroup', this.request?.discountGroupType);
  }

  getResponseDeliveryLabel(): string {
    return this.request?.responseDeliveryMethod === 'Unknown'
      ? '-'
      : this.irFormLabels.enumValue('responseDelivery', this.request?.responseDeliveryMethod);
  }

  getInvoiceDeliveryLabel(): string {
    return this.request?.invoiceDeliveryMethod === 'Unknown'
      ? '-'
      : this.irFormLabels.enumValue('invoiceDelivery', this.request?.invoiceDeliveryMethod);
  }

  getInvoiceDeliveryDetailLabel(): string {
    switch (this.request?.invoiceDeliveryMethod) {
      case 'Email':
        return this.irFormLabels.field('invoiceDelivery', 'detailEmail');
      case 'EAddress':
        return this.irFormLabels.field('invoiceDelivery', 'detailEAddress');
      case 'PostalMail':
        return this.irFormLabels.field('invoiceDelivery', 'detailAddress');
      default:
        return '';
    }
  }

  getResponseDeliveryDetailLabel(): string {
    switch (this.request?.responseDeliveryMethod) {
      case 'Email':
        return this.irFormLabels.field('responseDelivery', 'detailEmail');
      case 'EAddress':
        return this.irFormLabels.field('responseDelivery', 'detailEAddress');
      case 'PostalMail':
        return this.irFormLabels.field('responseDelivery', 'detailAddress');
      default:
        return '';
    }
  }

  getPurposeLabel(): string {
    return this.irFormLabels.enumValue('purpose', this.request?.purpose);
  }

  getLegalFormDisplay(): string {
    const raw = this.request?.legalEntity?.type;
    if (!raw) return '-';
    return this.getLegalEntityFormName(raw);
  }

  getLegalEntityFormName(code: string): string {
    if (!code) return '-';
    return this.legalEntityFormOptions.find((o) => o.code === code)?.name || code;
  }

  /**
   * Turns a print block's flat line list into the containers the tab has always used. The contract
   * is a reading-order list — right for the PDF, which is linear — so the grouping the screen needs
   * is derived here: a run of Field lines is a grid, and a run that follows a Text or Heading is the
   * bordered detail card those answers have always sat in.
   */
  printItems(block: PrintBlock, sectionKey: string): PrintItem[] {
    const items: PrintItem[] = [];
    const grid: PrintGrid = sectionKey === 'basicData' ? 'basic' : 'two-col';
    let fields: PrintLine[] = [];
    let boxed = false;

    const flushFields = (): void => {
      if (fields.length === 0) return;
      const group: PrintItem = { kind: 'fields', lines: fields, grid };
      const last = items[items.length - 1];
      if (boxed && last?.kind === 'card') {
        last.items.push(group);
      } else if (boxed) {
        items.push({ kind: 'card', items: [group] });
      } else {
        items.push(group);
      }
      fields = [];
    };

    for (const line of block.lines) {
      if (line.kind === 'Field' && !this.isRestrictedAccessLine(line)) {
        fields.push(line);
        continue;
      }
      flushFields();
      // The restricted-access answer has always been a banner beside the grid, not a row in it.
      // It arrives as a Text line since LNA-2042 made it a sentence with an icon rather than a
      // "...: Jā" row, so the kind is not what identifies it - the tooltip is. Taking it before the
      // switch also keeps it from setting `boxed`, which a Text line does to open a detail card.
      if (this.isRestrictedAccessLine(line)) {
        items.push({ kind: 'banner', value: line.value ?? '', tooltipKey: line.tooltipKey ?? null });
        continue;
      }
      switch (line.kind) {
        case 'Text':
          items.push({ kind: 'text', value: line.value ?? '' });
          boxed = true;
          break;
        case 'Heading':
          if (block.key === 'requester') {
            items.push({ kind: 'card', items: [{ kind: 'heading', value: line.value ?? '', tooltipKey: line.tooltipKey ?? null, boxed: true }] });
            boxed = true;
          } else {
            items.push({ kind: 'heading', value: line.value ?? '', tooltipKey: line.tooltipKey ?? null, boxed: false });
            boxed = false;
          }
          break;
        case 'Paragraph':
          items.push({ kind: 'paragraph', value: line.value ?? '' });
          break;
        case 'Consent':
          items.push({ kind: 'consent', value: line.value ?? '' });
          break;
        case 'Table':
          items.push({ kind: 'table', line });
          break;
        case 'Documents':
          items.push({ kind: 'documents', line });
          break;
      }
    }
    flushFields();
    return items;
  }

  downloadPrintDocument(doc: PrintDocument): void {
    this.apiService.downloadFile(doc.fileId).subscribe({
      next: (data) => {
        const url = window.URL.createObjectURL(new Blob([data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.fileName;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => this.toastService.showRequestError()
    });
  }

  /**
   * Single seam for every card the request endpoints hand back. No refetch: each card-returning
   * endpoint in InformationRequestController re-queries GetBoById server-side, so the response
   * always carries a printView built from the saved state.
   */
  private applyCard(data: InformationRequestCard): void {
    this.request = data;
  }

  printSection(key: string): PrintSection | undefined {
    return this.request?.printView?.sections?.find((s) => s.key === key);
  }

  printLink(line: PrintLine): unknown[] | null {
    const link = line.linkTo;
    if (!link?.id) return null;
    if (link.target === 'client') return ['/klients', link.id];
    if (link.target === 'legalEntity') return ['/juridiska-persona', link.id];
    return null;
  }

  /** The banner and the full-width row the tab has always given these two rows. */
  printFieldClass(line: PrintLine): string {
    if (line.tooltipKey === 'infoRequestAboutSubject') return 'info-field full-width';
    return 'info-field';
  }

  isRestrictedAccessLine(line: PrintLine): boolean {
    return line.tooltipKey === 'infoRequestRestrictedAccess';
  }

  private isUserInResponsibleUnit(): boolean {
    const userUnits = this.authService.userSecurityInfo()?.structuralUnits?.map((su: { id: string }) => su.id) || [];
    return userUnits.includes(this.request?.responsibleStructuralUnitId || '');
  }

  startPreliminaryResearch(): void {
    if (!this.requestId || this.isActionLoading) return;
    const userUnits = getStructuralUnitsForClaim(
      this.authService.userSecurityInfo(),
      CLAIM_SCOPES['informationRequest'],
      CLAIM_TYPES['create']
    );
    if (userUnits.length > 1) {
      this.userStructuralUnits = userUnits;
      this.isSelectStructuralUnitModalOpen = true;
      return;
    }
    const structuralUnitId = userUnits.length === 1 ? userUnits[0].id : null;
    this.executeStartPreliminaryResearch(structuralUnitId);
  }

  confirmSelectStructuralUnit(structuralUnitId: string | void): void {
    this.isSelectStructuralUnitModalOpen = false;
    if (typeof structuralUnitId !== 'string') return;
    this.executeStartPreliminaryResearch(structuralUnitId);
  }

  cancelSelectStructuralUnit(): void {
    this.isSelectStructuralUnitModalOpen = false;
  }

  private executeStartPreliminaryResearch(structuralUnitId: string | null): void {
    if (!this.requestId) return;
    this.isActionLoading = true;
    this.apiService.startPreliminaryResearch(this.requestId, structuralUnitId).subscribe({
      next: (data) => {
        this.applyCard(data);
        this.isActionLoading = false;
        this.toastService.showSuccess('preliminaryResearchStarted', '', { shouldTranslate: true });
      },
      error: () => {
        this.isActionLoading = false;
      }
    });
  }

  onOtherActionSelected(option: string): void {
    if (option === KEY_ACTION_START_PROCESSING) {
      this.startProcessingForm.reset();
      this.modalService.open(this.startProcessingModalRef, {
        backdrop: 'static',
        keyboard: false,
        windowClass: 'start-processing-modal'
      });
    } else if (option === KEY_ACTION_CANCEL) {
      this.cancelForm.reset();
      this.modalService.open(this.cancelModalRef, {
        backdrop: 'static',
        keyboard: false,
        windowClass: 'start-processing-modal'
      });
    } else if (option === KEY_ACTION_REDIRECT) {
      this.openForwardModal();
    } else if (option === KEY_ACTION_REASSIGN) {
      this.openHandOverModal();
    } else if (option === KEY_ACTION_REQUEST_ADDITIONAL_INFO) {
      this.isRequestAdditionalInfoModalOpen = true;
    }
  }

  openCompleteModal(): void {
    if (!this.request?.responseDocumentTypes?.length) {
      this.toastService.showDanger('infoRequestCompleteResponseDocTypeError', '', { shouldTranslate: true });
      return;
    }
    this.completeForm.reset();
    this.modalService.open(this.completeModalRef, {
      backdrop: 'static',
      keyboard: false,
      windowClass: 'start-processing-modal'
    });
  }

  confirmComplete(): void {
    if (!this.requestId || this.isActionLoading) return;
    const args: CompleteInformationRequestRequest = {
      note: this.completeForm.get('note')?.value || null
    };
    this.isActionLoading = true;
    this.apiService.completeInformationRequest(this.requestId, args).subscribe({
      next: (data) => {
        this.applyCard(data);
        this.isActionLoading = false;
        this.modalService.dismissAll();
        this.completeForm.reset();
        this.refreshTaskList();
        this.toastService.showSuccess('infoRequestCompleted', '', { shouldTranslate: true });
      },
      error: (err) => {
        this.isActionLoading = false;
        console.error('Complete info request error:', err);
        const responseDocTypeError = err?.error?.errors?.ResponseDocumentTypes?.[0];
        if (responseDocTypeError) {
          this.toastService.showDanger('infoRequestCompleteResponseDocTypeError', '', { shouldTranslate: true });
        } else {
          this.toastService.showDanger('infoRequestCompleteError', '', { shouldTranslate: true });
        }
      }
    });
  }

  cancelCompleteModal(): void {
    this.modalService.dismissAll();
    this.completeForm.reset();
  }

  confirmCancel(): void {
    if (!this.requestId || this.isActionLoading || this.cancelForm.invalid) return;
    const args: CancelBoInformationRequestRequest = {
      note: this.cancelForm.get('note')?.value
    };
    this.isActionLoading = true;
    this.apiService.cancelInformationRequest(this.requestId, args).subscribe({
      next: (data) => {
        this.applyCard(data);
        this.isActionLoading = false;
        this.modalService.dismissAll();
        this.cancelForm.reset();
        this.refreshTaskList();
        this.toastService.showSuccess('infoRequestCancelled', '', { shouldTranslate: true });
      },
      error: (err) => {
        this.isActionLoading = false;
        console.error('Cancel info request error:', err);
        this.toastService.showDanger('infoRequestCancelError', '', { shouldTranslate: true });
      }
    });
  }

  cancelCancelModal(): void {
    this.modalService.dismissAll();
    this.cancelForm.reset();
  }

  // === Forward to structural unit ===

  openForwardModal(): void {
    if (!this.request) return;
    if (this.request.coResponsibleStructuralUnits && this.request.coResponsibleStructuralUnits.length > 0) {
      this.toastService.showDanger('infoRequestForwardBlockedByCoResponsible', '', { shouldTranslate: true });
      return;
    }
    this.forwardForm.reset();
    this.modalService.open(this.forwardModalRef, {
      backdrop: 'static',
      keyboard: false,
      windowClass: 'start-processing-modal'
    });
  }

  get forwardStructuralUnitOptions(): StructuralUnitMinimalListItem[] {
    if (!this.request) return this.structuralUnits;
    return this.structuralUnits.filter((u) => u.id !== this.request!.responsibleStructuralUnitId);
  }

  confirmForward(): void {
    if (!this.requestId || this.isActionLoading || this.forwardForm.invalid) return;
    const args: ForwardInformationRequestRequest = {
      targetStructuralUnitId: this.forwardForm.get('targetStructuralUnitId')?.value,
      justification: this.forwardForm.get('justification')?.value || null
    };
    this.isActionLoading = true;
    this.apiService.forwardIrToStructuralUnit(this.requestId, args).subscribe({
      next: (data) => {
        this.applyCard(data);
        this.isActionLoading = false;
        this.modalService.dismissAll();
        this.forwardForm.reset();
        this.toastService.showSuccess('infoRequestForwarded', '', { shouldTranslate: true });
      },
      error: (err) => {
        this.isActionLoading = false;
        console.error('Forward info request error:', err);
        this.toastService.showDanger('infoRequestForwardError', '', { shouldTranslate: true });
      }
    });
  }

  // === Hand over to employee ===

  openHandOverModal(): void {
    this.handOverForm.reset();
    this.modalService.open(this.handOverModalRef, {
      backdrop: 'static',
      keyboard: false,
      windowClass: 'start-processing-modal'
    });
  }

  onHandOverUserSelected(userId: string): void {
    this.handOverForm.get('targetUserId')?.setValue(userId);
  }

  confirmHandOver(): void {
    if (!this.requestId || this.isActionLoading || this.handOverForm.invalid) return;
    const args: HandOverInformationRequestRequest = {
      targetUserId: this.handOverForm.get('targetUserId')?.value,
      justification: this.handOverForm.get('justification')?.value
    };
    this.isActionLoading = true;
    this.apiService.handOverIrToEmployee(this.requestId, args).subscribe({
      next: (data) => {
        this.applyCard(data);
        this.isActionLoading = false;
        this.modalService.dismissAll();
        this.handOverForm.reset();
        this.toastService.showSuccess('infoRequestHandedOver', '', { shouldTranslate: true });
      },
      error: (err) => {
        this.isActionLoading = false;
        console.error('Hand over info request error:', err);
        this.toastService.showDanger('infoRequestHandOverError', '', { shouldTranslate: true });
      }
    });
  }

  onDeadlineDateSelected(date: string): void {
    this.startProcessingForm.get('deadlineDate')?.setValue(date);
  }

  confirmStartProcessing(): void {
    if (!this.requestId || this.isActionLoading) return;
    const dateValue = this.startProcessingForm.get('deadlineDate')?.value;
    if (!dateValue) return;
    const isoDate = dateValue + 'T00:00:00';
    this.isActionLoading = true;
    this.apiService.startInfoRequestProcessing(this.requestId, isoDate).subscribe({
      next: (data) => {
        this.applyCard(data);
        this.isActionLoading = false;
        this.modalService.dismissAll();
        this.startProcessingForm.reset();
        this.toastService.showSuccess('infoRequestProcessingStarted', '', { shouldTranslate: true });
      },
      error: (err) => {
        this.isActionLoading = false;
        const validationMessage = this.extractValidationErrors(err);
        if (validationMessage) {
          this.toastService.showDanger(validationMessage);
        } else {
          this.toastService.showDanger('infoRequestProcessingError', '', { shouldTranslate: true });
        }
      }
    });
  }

  private readonly VALIDATION_ERROR_MAP: Record<string, string> = {
    'Must complete by date must not be earlier than today.': 'mustCompleteByDateInPast',
    'Must complete by date cannot be removed.': 'mustCompleteByDateCannotBeRemoved',
    'Must use StartProcessing for first-time set': 'mustCompleteByDateUseStartProcessing',
    'Deadline date is required when Other deadline is selected.': 'deadlineDateRequiredWhenOther',
    'Deadline date must not be earlier than today.': 'deadlineDateInPast'
  };

  private extractValidationErrors(err: any): string | null {
    const errors = err?.error?.errors;
    if (!errors) return null;
    const messages: string[] = [];
    for (const key of Object.keys(errors)) {
      if (Array.isArray(errors[key])) {
        for (const msg of errors[key]) {
          const translationKey = this.VALIDATION_ERROR_MAP[msg];
          messages.push(translationKey ? this.translateService.instant(`components.notification.${translationKey}`) : msg);
        }
      }
    }
    return messages.length > 0 ? messages.join('. ') : null;
  }

  cancelStartProcessing(): void {
    this.modalService.dismissAll();
    this.startProcessingForm.reset();
  }

  // Basic info edit mode
  enterBasicInfoEdit(): void {
    this.basicInfoForm.patchValue({
      submissionChannel: this.request?.submissionChannel || '',
      serviceDeadline: this.request?.serviceDeadline || '',
      deadlineDate: this.request?.deadlineDate ? this.request.deadlineDate.split('T')[0] : '',
      mustCompleteByDate: this.request?.mustCompleteByDate ? this.request.mustCompleteByDate.split('T')[0] : '',
      letterNumber: this.request?.letterNumber || '',
      aboutSubject: this.request?.aboutSubject || '',
      requiresRestrictedAccess: this.request?.requiresRestrictedAccess || false
    });
    if (this.request?.submissionChannel === 'Portal') {
      this.basicInfoForm.get('submissionChannel')?.disable();
    } else {
      this.basicInfoForm.get('submissionChannel')?.enable();
    }
    this.isBasicInfoEditMode = true;
  }

  cancelBasicInfoEdit(): void {
    this.isBasicInfoEditMode = false;
  }

  saveBasicInfo(): void {
    if (this.basicInfoForm.invalid || !this.requestId) return;
    const val = this.basicInfoForm.getRawValue();
    if (val.serviceDeadline === 'Other' && !val.deadlineDate) {
      this.toastService.showDanger('deadlineDateRequiredWhenOther', '', { shouldTranslate: true });
      return;
    }
    const args: UpdateBoInformationRequestRequest = {
      ...this.preserveAllRequestFields(),
      submissionChannel: val.submissionChannel,
      serviceDeadline: val.serviceDeadline,
      letterNumber: val.letterNumber || null,
      aboutSubject: val.aboutSubject || null,
      requiresRestrictedAccess: val.requiresRestrictedAccess
    };
    if (val.serviceDeadline === 'Other' && val.deadlineDate) {
      args.serviceDeadlineDate = val.deadlineDate + 'T00:00:00Z';
    } else {
      args.serviceDeadlineDate = null;
    }
    if (val.mustCompleteByDate) {
      args.mustCompleteByDate = val.mustCompleteByDate + 'T00:00:00Z';
    }
    this.isActionLoading = true;
    this.apiService.updateInformationRequest(this.requestId, args).subscribe({
      next: (data) => {
        this.applyCard(data);
        this.isBasicInfoEditMode = false;
        this.isActionLoading = false;
        this.toastService.showSuccess('basicInfoUpdated', '', { shouldTranslate: true });
      },
      error: (err) => {
        this.isActionLoading = false;
        const validationMessage = this.extractValidationErrors(err);
        if (validationMessage) {
          this.toastService.showDanger(validationMessage);
        } else {
          this.toastService.showDanger('basicInfoUpdateError', '', { shouldTranslate: true });
        }
      }
    });
  }

  private preserveAllRequestFields(): Partial<UpdateBoInformationRequestRequest> {
    const r = this.request;
    if (!r) return {};
    return {
      submissionChannel: r.submissionChannel || undefined,
      letterNumber: r.letterNumber || null,
      aboutSubject: r.aboutSubject || null,
      additionalText: r.additionalText || null,
      topicType: r.topicType || undefined,
      topicVersionId: r.topicVersionId || null,
      topicFieldData: r.topicFieldData || null,
      blockCount: r.blockCount || 1,
      requiresRestrictedAccess: r.requiresRestrictedAccess || false,
      serviceDeadline: r.serviceDeadline || undefined,
      serviceDeadlineDate: r.serviceDeadlineDate || r.deadlineDate || null,
      mustCompleteByDate: r.mustCompleteByDate || null,
      phone: r.phone || null,
      requesterRelationType: r.requesterRelationType || undefined,
      requesterForOtherPersonType: r.requesterForOtherPersonType || undefined,
      requesterForOtherPersonData: r.requesterForOtherPersonData || null,
      discountGroupType: r.discountGroupType || undefined,
      discountGroupData: r.discountGroupData || null,
      payerType: r.payerType || undefined,
      payerData: r.payerData || null,
      // The handler clears this one without a HasValue guard (LNA-1940), so leaving it out of a
      // full-state PUT wipes "Bezmaksas pamatojums" on every Pamatdati save.
      payerFreeOfChargeReason: r.payerFreeOfChargeReason || null,
      invoiceDeliveryMethod: r.invoiceDeliveryMethod || undefined,
      invoiceDeliveryDetail: r.invoiceDeliveryDetail || null,
      invoiceDeliveryStructuralUnitId: r.invoiceDeliveryStructuralUnitId || null,
      purpose: r.purpose || undefined,
      purposeInstitutionName: r.purposeInstitutionName || null,
      vsaaFlag: r.vsaaFlag || false,
      responseDeliveryMethod: r.responseDeliveryMethod || undefined,
      responseDeliveryDetail: r.responseDeliveryDetail || null,
      responseDeliveryStructuralUnitId: r.responseDeliveryStructuralUnitId || null,
      notes: r.notes || null,
      contactPersonName: r.contactPersonName || null,
      contactPersonSurname: r.contactPersonSurname || null,
      contactPersonEmail: r.contactPersonEmail || null,
      contactPersonPhone: r.contactPersonPhone || null
    };
  }

  onBasicInfoDeadlineDateSelected(date: string): void {
    this.basicInfoForm.get('deadlineDate')?.setValue(date);
  }

  onMustCompleteByDateSelected(date: string): void {
    this.basicInfoForm.get('mustCompleteByDate')?.setValue(date);
  }

  // Request (topic) edit mode
  enterRequestEdit(): void {
    this.isRequestEditMode = true;
  }

  cancelRequestEdit(): void {
    this.isRequestEditMode = false;
  }

  /** The request's own fields, its documents, then the reload: a second press in between would upload them twice. */
  get isRequestSaving(): boolean {
    return this.isRequestDocumentsSaving || !!this.topicFormEdit?.isSaving;
  }

  saveRequest(): void {
    if (this.isRequestSaving) return;
    this.topicFormEdit?.saveInlineEdit();
  }

  onRequestSaved(data: InformationRequestCard): void {
    const fileObservables = this.collectRequestFileObservables();
    if (fileObservables.length === 0) {
      this.finishRequestEdit();
      return;
    }

    // A refused file action (the server's per-kind cap above all) used to cancel its siblings and
    // reload without a word, so the operator saw a document quietly missing (LNA-1850). Let every
    // action finish, then name the first failure. Saglabāt stays disabled until the reload has landed:
    // until then an upload that went through is still staged, and a retry would send it again.
    this.isRequestDocumentsSaving = true;
    const release = (): void => {
      this.isRequestDocumentsSaving = false;
    };
    forkJoin(isolateFileActions(fileObservables)).subscribe((results) => {
      if (countFileActionFailures(results) === 0) {
        this.finishRequestEdit(release);
        return;
      }

      // The request's own fields are stored by now; only documents are missing. Closing the section
      // brought the old description back under a green toast, just like LNAEP-1076, and lost what the
      // operator typed (LNA-2047). Keep it open so Saglabāt retries. The reload still runs: it turns
      // the uploads that did go through into existing files, so the retry does not send them again.
      this.toastService.showRequestError(firstFileActionError(results));
      this.loadData(release);
    });
  }

  private finishRequestEdit(onReloaded?: () => void): void {
    this.toastService.showSuccess('infoRequestSectionUpdated', '', { shouldTranslate: true });
    this.isRequestEditMode = false;
    this.loadData(onReloaded);
  }

  private collectRequestFileObservables(): Observable<any>[] {
    const observables: Observable<any>[] = [];
    if (!this.requestDocumentUpload) return observables;
    const deletedIds = [...this.requestDocumentUpload.pendingDeletions];
    const sub = this.requestDocumentUpload.uploading.subscribe((obs: Observable<any>[]) => {
      observables.push(...obs);
    });
    this.requestDocumentUpload.handleFileRequests();
    sub.unsubscribe();
    this.removeDeletedRequestDocuments(deletedIds);
    return observables;
  }

  /**
   * The file list forgets its pending deletions as soon as it hands them over, while `documentFiles`
   * still listed them until the reload. In the section that now stays open after a refusal, the removed
   * file came back, and the list then dropped a staged replacement of the same name and gave it the old
   * note (LNA-2047). Drop them from the card now, as the wizard does; the reload brings back any whose
   * deletion was refused.
   */
  private removeDeletedRequestDocuments(deletedIds: string[]): void {
    if (deletedIds.length === 0 || !this.request) return;
    this.request = {
      ...this.request,
      documents: (this.request.documents || []).filter((d) => !deletedIds.includes(d.outerId!))
    };
  }

  // Client & Payer edit mode
  enterClientPayerEdit(): void {
    this.isClientPayerEditMode = true;
  }

  cancelClientPayerEdit(): void {
    this.isClientPayerEditMode = false;
  }

  saveClientPayer(): void {
    this.clientPayerFormEdit?.saveInlineEdit();
  }

  onClientPayerSaved(data: InformationRequestCard): void {
    this.isClientPayerEditMode = false;
    this.loadData();
  }

  get canEditRequest(): boolean {
    if (!this.request) return false;
    return this.request.backofficeStatus !== 'Completed' && this.request.backofficeStatus !== 'Cancelled';
  }

  navigateToEdit(): void {
    this.router.navigate([`/pakalpojumi/informacijas-pieprasijums/${this.requestId}`], {
      queryParams: this.buildEditQueryParams()
    });
  }

  navigateToEditStep(step: number): void {
    this.router.navigate([`/pakalpojumi/informacijas-pieprasijums/${this.requestId}`], {
      queryParams: { ...this.buildEditQueryParams(), solis: step }
    });
  }

  private buildEditQueryParams(): Record<string, string> {
    const params: Record<string, string> = {
      clientType: this.isOrganization ? '2' : '1'
    };
    if (this.request?.clientId) params['clientId'] = this.request.clientId;
    if (this.request?.legalEntityId) params['legalEntityId'] = this.request.legalEntityId;
    return params;
  }

  // === Response Tab ===

  get isResponseTabReadonly(): boolean {
    if (!this.request) return true;
    return RESPONSE_TAB_READONLY_STATUSES.includes(this.request.backofficeStatus);
  }

  // Hide "Nosūtīt atbildi uz e-pastu", "Nosūtīt atbildi uz Portālu", "Atzīmēt nosūtīšanu"
  // once the IR is closed (Izpildīts / Atsaukts) or just bounced to another office
  // (Jauns (Pārvirzīts)) — at those points sending is no longer the operator's concern.
  get isSendingBlocked(): boolean {
    if (!this.request) return true;
    return (
      this.request.backofficeStatus === 'Completed' ||
      this.request.backofficeStatus === 'Cancelled' ||
      this.request.backofficeStatus === 'NewRedirected'
    );
  }

  // --- Document Types ---

  enterDocumentTypeEdit(): void {
    this.documentTypeRows = (this.request?.responseDocumentTypes || []).map((dt) => ({
      valueId: dt.valueId,
      count: dt.count ?? null
    }));
    if (this.documentTypeRows.length === 0) {
      this.addDocumentTypeRow();
    }
    this.isDocumentTypeEditMode = true;
  }

  cancelDocumentTypeEdit(): void {
    this.isDocumentTypeEditMode = false;
    this.documentTypeRows = [];
  }

  addDocumentTypeRow(): void {
    this.documentTypeRows.push({ valueId: '', count: null });
  }

  removeDocumentTypeRow(index: number): void {
    this.documentTypeRows.splice(index, 1);
  }

  getAvailableDocumentTypes(currentIndex: number): ClassifierValueCard[] {
    const usedIds = this.documentTypeRows.filter((_, i) => i !== currentIndex).map((r) => r.valueId);
    return this.documentTypeOptions.filter((opt) => !usedIds.includes(opt.id));
  }

  isDocumentCopyValue(valueId: string): boolean {
    return this.documentTypeOptions.find((opt) => opt.id === valueId)?.code === DOCUMENT_COPY_VALUE_CODE;
  }

  onDocumentTypeKindChange(index: number, valueId: string): void {
    this.documentTypeRows[index].valueId = valueId;
    if (this.isDocumentCopyValue(valueId)) {
      this.documentTypeRows[index].count = this.documentTypeRows[index].count || 1;
    } else {
      this.documentTypeRows[index].count = null;
    }
  }

  saveDocumentTypes(): void {
    if (!this.requestId || this.isActionLoading) return;
    const validRows = this.documentTypeRows.filter((r) => r.valueId);
    this.isActionLoading = true;
    this.apiService.setResponseDocumentTypes(this.requestId, validRows).subscribe({
      next: (data) => {
        this.applyCard(data);
        this.isActionLoading = false;
        this.isDocumentTypeEditMode = false;
      },
      error: (err) => {
        this.isActionLoading = false;
        console.error('Save response document types error:', err);
        this.toastService.showDanger('infoRequestSaveDocTypesError', '', { shouldTranslate: true });
      }
    });
  }

  // --- Namejs Documents ---

  requestNamejsReply(): void {
    if (!this.requestId || this.isActionLoading) return;
    this.isActionLoading = true;
    this.apiService.requestNamejsDocument(this.requestId, 'Reply').subscribe({
      next: (data) => {
        this.applyCard(data);
        this.isActionLoading = false;
        this.toastService.showSuccess('namejaPrepareSuccess', '', { shouldTranslate: true });
      },
      error: (err) => this.handleNamejsPrepareError(err, 'Reply')
    });
  }

  requestNamejsForwarding(): void {
    if (!this.requestId || this.isActionLoading) return;
    this.isActionLoading = true;
    this.apiService.requestNamejsDocument(this.requestId, 'ForwardingLetter').subscribe({
      next: (data) => {
        this.applyCard(data);
        this.isActionLoading = false;
        this.toastService.showSuccess('namejaPrepareSuccess', '', { shouldTranslate: true });
      },
      error: (err) => this.handleNamejsPrepareError(err, 'ForwardingLetter')
    });
  }

  // Surface the BE failure (auth, network, validation, upstream Namejs error) as a toast.
  // Prefer the BE-provided `detail` (which carries the Latvian Namejs message via
  // NamejsApiExceptionHandler); fall back to the generic translated key when absent.
  private handleNamejsPrepareError(err: any, kind: 'Reply' | 'ForwardingLetter'): void {
    this.isActionLoading = false;
    console.error(`requestNamejs${kind} error:`, err);
    const detail = this.extractBeErrorDetail(err);
    if (detail) {
      this.toastService.showDanger(detail, '', { shouldTranslate: false });
    } else {
      this.toastService.showDanger('namejaPrepareFailed', '', { shouldTranslate: true });
    }
  }

  retrieveFromNameja(kind: 'Reply' | 'ForwardingLetter'): void {
    if (!this.requestId || this.isActionLoading) return;
    this.isActionLoading = true;
    this.apiService.retrieveNamejsDocument(this.requestId, kind).subscribe({
      next: (data) => {
        this.applyCard(data);
        this.isActionLoading = false;
      },
      error: (err) => {
        console.error('retrieveFromNameja error:', err);
        this.isActionLoading = false;
        const detail = this.extractBeErrorDetail(err);
        if (detail) {
          this.toastService.showDanger(detail, '', { shouldTranslate: false });
        } else {
          this.toastService.showDanger('namejaRetrieveError', '', { shouldTranslate: true });
        }
      }
    });
  }

  // ProblemDetails shape: prefer `detail`, fall back to first message in `errors` map.
  private extractBeErrorDetail(err: any): string {
    const detail = err?.error?.detail;
    if (typeof detail === 'string' && detail.length > 0) return detail;
    const errors = err?.error?.errors;
    if (errors && typeof errors === 'object') {
      for (const key of Object.keys(errors)) {
        const msgs = errors[key];
        if (Array.isArray(msgs) && msgs.length > 0 && typeof msgs[0] === 'string') return msgs[0];
      }
    }
    return '';
  }

  getNamejsStatusLabel(status: string): string {
    return this.translateService.instant(`pages.viewInfoRequest.response.namejsStatus.${status}`);
  }

  downloadNamejsFile(file: { outerId?: string | null; uploadFileName?: string | null }): void {
    if (!file?.outerId) return;
    this.apiService.downloadFile(file.outerId).subscribe({
      next: (data) => {
        const blob = new Blob([data], { type: 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.uploadFileName ?? 'namejs-document';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('downloadNamejsFile error:', err);
        this.toastService.showRequestError();
      }
    });
  }

  downloadPdf(): void {
    this.apiService.downloadInformationRequestPdf(this.requestId).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.request?.code ?? this.requestId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: () => this.toastService.showRequestError()
    });
  }

  // --- Sending Events ---

  openSendEmailModal(parentType: SendingEventParentType, parentId: string): void {
    this.sendingEventParentType = parentType;
    this.sendingEventParentId = parentId;
    this.sendEmailForm.reset();

    let clientEmail = '';
    if (this.request?.responseDeliveryMethod === 'Email' && this.request?.responseDeliveryDetail) {
      clientEmail = this.request.responseDeliveryDetail;
    } else if (this.isOrganization) {
      clientEmail = this.request?.contactPersonEmail || this.request?.legalEntity?.email || '';
    } else {
      clientEmail = this.request?.client?.email || '';
    }

    const code = this.request?.code || '';
    const topicName = this.request?.topicVersion?.name || '';
    const defaultSubject = `Atbilde uz pieprasījumu Nr. ${code}, Latvijas Nacionālais arhīvs`;

    this.sendEmailExistingFiles = this.getResponseFiles(parentType, parentId);

    const unit = this.request?.responsibleStructuralUnit;
    const unitInfo = unit
      ? this.buildUnitContactInfo(unit.name, unit.phone, unit.email)
      : this.request?.responsibleStructuralUnitName || '';
    this.patchSendEmailForm(clientEmail, defaultSubject, code, topicName, unitInfo);

    this.modalService.open(this.sendEmailModalRef, {
      backdrop: 'static',
      keyboard: false,
      windowClass: 'send-email-modal'
    });
  }

  private buildUnitContactInfo(name: string, phone?: string | null, email?: string | null): string {
    const parts = [name];
    if (phone) parts.push(phone);
    if (email) parts.push(email);
    return parts.join(', ');
  }

  private patchSendEmailForm(clientEmail: string, subject: string, code: string, topicName: string, unitInfo: string): void {
    const topicNameEn = this.request?.topicVersion?.nameEn || topicName;
    const lvBody = `Labdien!\nNosūtām atbildi (skat. pielikumā) uz Jūsu pieprasījumu Nr. ${code}, pieprasījuma tēma "${topicName}".\nPaldies, ka izmantojat mūsu pakalpojumus!\nCieņā\nLatvijas Nacionālais arhīvs\n${unitInfo}\nŠis ir automātiski ģenerēts ziņojums, lūdzam neatbildēt.`;
    const enBody = `Dear Sir/Madam,\nPlease find attached the response to your request No. ${code}, topic "${topicNameEn}".\nThank you for using our services!\nYours sincerely,\nNational Archives of Latvia\n${unitInfo}\nThis is an automatically generated message, please do not reply.`;
    const defaultBody = `${lvBody}\n\n---\n\n${enBody}`;
    this.sendEmailForm.patchValue({
      addressDetail: clientEmail,
      emailSubject: subject,
      emailBody: defaultBody
    });
  }

  private getResponseFiles(parentType: SendingEventParentType, parentId: string): FileCard[] {
    if (parentType === 'NamejsReply' && this.request?.namejsReply?.id === parentId) {
      return this.request.namejsReply.files || [];
    }
    if (parentType === 'NamejsForwarding' && this.request?.namejsForwarding?.id === parentId) {
      return this.request.namejsForwarding.files || [];
    }
    if (parentType === 'OtherResponse') {
      const other = this.request?.otherResponses?.find((o) => o.id === parentId);
      if (other) {
        return other.files || [];
      }
    }
    return [];
  }

  confirmSendEmail(): void {
    if (!this.requestId || !this.sendingEventParentType || !this.sendingEventParentId || this.isActionLoading) return;
    if (this.sendEmailForm.invalid) return;
    const val = this.sendEmailForm.getRawValue();
    this.isActionLoading = true;
    this.apiService
      .sendResponseByEmail({
        informationRequestId: this.requestId,
        parentType: this.sendingEventParentType,
        parentId: this.sendingEventParentId,
        recipient: val.addressDetail,
        emailSubject: val.emailSubject,
        emailBody: val.emailBody
      })
      .subscribe({
        next: () => {
          this.isActionLoading = false;
          this.modalService.dismissAll();
          this.loadData();
        },
        error: () => {
          this.isActionLoading = false;
        }
      });
  }

  openSendPortalModal(parentType: SendingEventParentType, parentId: string): void {
    this.sendingEventParentType = parentType;
    this.sendingEventParentId = parentId;
    this.sendPortalForm.reset();
    this.sendPortalExistingFiles = this.getResponseFiles(parentType, parentId);
    this.modalService.open(this.sendPortalModalRef, {
      backdrop: 'static',
      keyboard: false,
      windowClass: 'start-processing-modal'
    });
  }

  confirmSendPortal(): void {
    if (!this.requestId || !this.sendingEventParentType || !this.sendingEventParentId || this.isActionLoading) return;
    this.isActionLoading = true;
    this.apiService
      .sendResponseToPortal(this.requestId, {
        parentType: this.sendingEventParentType,
        parentId: this.sendingEventParentId,
        clientMessage: this.sendPortalForm.get('clientMessage')?.value || null
      })
      .subscribe({
        next: (data) => {
          this.applyCard(data);
          this.isActionLoading = false;
          this.modalService.dismissAll();
        },
        error: () => {
          this.isActionLoading = false;
        }
      });
  }

  openMarkSentModal(parentType: SendingEventParentType, parentId: string): void {
    this.sendingEventParentType = parentType;
    this.sendingEventParentId = parentId;
    this.markSentForm.reset();
    this.modalService.open(this.markSentModalRef, {
      backdrop: 'static',
      keyboard: false,
      windowClass: 'send-email-modal'
    });
  }

  private resolveResponseAddressDefault(method: string): string {
    const req = this.request;
    if (!req || !method) return '';
    if (method === 'Email') {
      if (req.responseDeliveryMethod === 'Email' && req.responseDeliveryDetail) {
        return req.responseDeliveryDetail;
      }
      return req.contactPersonEmail || req.legalEntity?.email || req.clientInfo?.email || req.client?.email || '';
    }
    if (method === 'PostalMail') {
      if (req.responseDeliveryMethod === 'PostalMail' && req.responseDeliveryDetail) {
        return req.responseDeliveryDetail;
      }
      const invoiceAddress = req.invoiceDeliveryMethod === 'PostalMail' ? req.invoiceDeliveryDetail : null;
      return req.clientInfo?.legalAddress || req.legalEntity?.legalAddress || invoiceAddress || '';
    }
    if (method === 'Archive') {
      return req.responseDeliveryStructuralUnitName || req.invoiceDeliveryStructuralUnitName || req.responsibleStructuralUnitName || '';
    }
    return '';
  }

  getMarkSentDeliveryOptions(): { value: string; label: string }[] {
    const base = [
      { value: 'Email', label: 'pages.viewInfoRequest.response.deliveryMethod.Email' },
      { value: 'EAddress', label: 'pages.viewInfoRequest.response.deliveryMethod.EAddress' },
      { value: 'PostalMail', label: 'pages.viewInfoRequest.response.deliveryMethod.PostalMail' },
      { value: 'Archive', label: 'pages.viewInfoRequest.response.deliveryMethod.Archive' }
    ];
    if (this.sendingEventParentType === 'NamejsReply' || this.sendingEventParentType === 'NamejsForwarding') {
      return base;
    }
    return base;
  }

  confirmMarkSent(): void {
    if (!this.requestId || !this.sendingEventParentType || !this.sendingEventParentId || this.isActionLoading) return;
    if (this.markSentForm.invalid) return;
    const val = this.markSentForm.getRawValue();
    this.isActionLoading = true;
    this.apiService
      .recordSendingEvent(this.requestId, {
        parentType: this.sendingEventParentType,
        parentId: this.sendingEventParentId,
        actualDeliveryMethod: val.actualDeliveryMethod,
        addressDetail: val.addressDetail || null,
        emailSubject: val.emailSubject || null,
        emailBody: val.emailBody || null,
        backofficeNote: val.backofficeNote || null
      })
      .subscribe({
        next: (data) => {
          this.applyCard(data);
          this.isActionLoading = false;
          this.modalService.dismissAll();
        },
        error: (err) => {
          this.isActionLoading = false;
          this.toastService.showRequestError(err);
        }
      });
  }

  // --- Other Responses (Cita atbilde) ---

  startAddOtherResponse(): void {
    this.newOtherResponseDescription = '';
    this.otherResponseTouched = false;
    this.isAddingOtherResponse = true;
    setTimeout(() => {
      document.getElementById('new-other-response')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  cancelAddOtherResponse(): void {
    this.isAddingOtherResponse = false;
    this.newOtherResponseDescription = '';
    this.newOtherResponseFiles = [];
  }

  confirmAddOtherResponse(): void {
    if (!this.requestId || this.isActionLoading || !this.newOtherResponseDescription.trim()) return;
    this.isActionLoading = true;
    this.apiService.createOtherResponse(this.requestId, this.newOtherResponseDescription.trim()).subscribe({
      next: (data) => {
        this.applyCard(data);
        const createdOther = (data.otherResponses || []).at(-1);
        this.isActionLoading = false;
        if (createdOther) {
          this.newOtherResponseId = createdOther.id;
          this.isSubmittingNewOtherResponseFiles = true;
        } else {
          this.isAddingOtherResponse = false;
          this.newOtherResponseDescription = '';
          this.loadData();
        }
      },
      error: () => {
        this.isActionLoading = false;
      }
    });
  }

  private notifyUploadFailures(results: unknown[]): void {
    const failed = countUploadFailures(results);
    if (failed > 0) {
      const message = this.translateService.instant('components.notification.fileUploadPartialError', {
        failed,
        total: results.length
      });
      this.toastService.showDanger(message, '', { shouldTranslate: false });
    }
  }

  handleNewOtherResponseFileUploads(uploads$: Observable<any>[]): void {
    if (uploads$.length === 0) {
      this.finishAddOtherResponse();
      return;
    }
    runUploads(uploads$).subscribe(results => {
      this.notifyUploadFailures(results);
      this.finishAddOtherResponse();
    });
  }

  private finishAddOtherResponse(): void {
    this.isSubmittingNewOtherResponseFiles = false;
    this.isAddingOtherResponse = false;
    this.newOtherResponseDescription = '';
    this.newOtherResponseId = '';
    this.newOtherResponseFiles = [];
    this.loadData();
  }

  deleteOtherResponse(otherId: string): void {
    if (!this.requestId || this.isActionLoading) return;
    this.isActionLoading = true;
    this.apiService.deleteOtherResponse(this.requestId, otherId).subscribe({
      next: (data) => {
        this.applyCard(data);
        this.isActionLoading = false;
      },
      error: () => {
        this.isActionLoading = false;
      }
    });
  }

  startEditOtherResponse(other: { id: string; description?: string | null }): void {
    this.editingOtherResponseId = other.id;
    this.editingOtherResponseDescription = other.description || '';
    this.editOtherResponseTouched = false;
  }

  cancelEditOtherResponse(): void {
    this.resetEditOtherResponse();
    this.loadData();
  }

  private resetEditOtherResponse(): void {
    this.editingOtherResponseId = null;
    this.editingOtherResponseDescription = '';
  }

  confirmEditOtherResponse(): void {
    if (!this.requestId || !this.editingOtherResponseId || this.isActionLoading) return;
    // Same rule as on create (and as the invoice edit): an other response can't be saved
    // without at least one file (LNA-1971).
    if ((this.editOtherResponseFileUpload?.totalFileCount ?? 0) === 0) {
      this.toastService.showDanger('otherResponseFileRequired', '', { shouldTranslate: true });
      return;
    }
    this.isActionLoading = true;
    this.isSubmittingOtherResponseFiles = true;
  }

  handleOtherResponseFileUploads(uploads$: Observable<any>[]): void {
    const saveText$: Observable<any> = this.apiService.updateOtherResponse(
      this.requestId,
      this.editingOtherResponseId!,
      this.editingOtherResponseDescription.trim()
    );
    forkJoin([saveText$, ...isolateUploads(uploads$)]).subscribe({
      next: results => {
        this.notifyUploadFailures(results.slice(1));
        this.isActionLoading = false;
        this.editingOtherResponseId = null;
        this.editingOtherResponseDescription = '';
        this.isSubmittingOtherResponseFiles = false;
        this.loadData();
      },
      error: () => {
        this.toastService.showRequestError();
        this.isActionLoading = false;
        this.isSubmittingOtherResponseFiles = false;
      }
    });
  }

  // --- Response Drafts (Atbildes sagatave) ---

  get canEditDrafts(): boolean {
    return !this.request?.namejsReply;
  }

  startAddDraft(): void {
    this.newDraftText = '';
    this.newDraftFiles = [];
    this.isAddingDraft = true;
  }

  cancelAddDraft(): void {
    this.isAddingDraft = false;
    this.newDraftText = '';
    this.newDraftFiles = [];
    this.draftTouched = false;
  }

  onDraftFilesSelected(event: NgxDropzoneChangeEvent): void {
    this.newDraftFiles = [...this.newDraftFiles, ...event.addedFiles];
  }

  removeDraftFile(index: number): void {
    this.newDraftFiles.splice(index, 1);
  }

  confirmAddDraft(): void {
    if (!this.requestId || this.isActionLoading || !this.newDraftText.trim()) return;
    this.isActionLoading = true;
    this.apiService.createResponseDraft(this.requestId, this.newDraftText.trim()).subscribe({
      next: (data) => {
        this.applyCard(data);
        const createdDraft = (data.responseDrafts || []).at(-1);
        this.isActionLoading = false;
        if (createdDraft) {
          this.newDraftId = createdDraft.id;
          this.isSubmittingNewDraftFiles = true;
        } else {
          this.isAddingDraft = false;
          this.newDraftText = '';
          this.loadData();
        }
      },
      error: () => {
        this.isActionLoading = false;
      }
    });
  }

  handleNewDraftFileUploads(uploads$: Observable<any>[]): void {
    if (uploads$.length === 0) {
      this.finishAddDraft();
      return;
    }
    runUploads(uploads$).subscribe(results => {
      this.notifyUploadFailures(results);
      this.finishAddDraft();
    });
  }

  private finishAddDraft(): void {
    this.isSubmittingNewDraftFiles = false;
    this.isAddingDraft = false;
    this.newDraftText = '';
    this.newDraftId = '';
    this.newDraftFiles = [];
    this.draftTouched = false;
    this.loadData();
  }

  startEditDraft(draft: ResponseDraftItem): void {
    this.editingDraftId = draft.id;
    this.editingDraftText = draft.text;
  }

  cancelEditDraft(): void {
    this.resetEditDraft();
    this.loadData();
  }

  private resetEditDraft(): void {
    this.editingDraftId = null;
    this.editingDraftText = '';
    this.editDraftTouched = false;
  }

  confirmEditDraft(): void {
    if (!this.requestId || !this.editingDraftId || this.isActionLoading || !this.editingDraftText.trim()) return;
    this.isActionLoading = true;
    this.isSubmittingDraftFiles = true;
  }

  handleDraftFileUploads(uploads$: Observable<any>[]): void {
    const saveText$: Observable<any> = this.apiService.updateResponseDraft(
      this.requestId,
      this.editingDraftId!,
      this.editingDraftText.trim()
    );
    forkJoin([saveText$, ...isolateUploads(uploads$)]).subscribe({
      next: results => {
        this.notifyUploadFailures(results.slice(1));
        this.isActionLoading = false;
        this.editingDraftId = null;
        this.editingDraftText = '';
        this.isSubmittingDraftFiles = false;
        this.loadData();
      },
      error: () => {
        this.toastService.showRequestError();
        this.isActionLoading = false;
        this.isSubmittingDraftFiles = false;
      }
    });
  }

  deleteDraft(draftId: string): void {
    if (!this.requestId || this.isActionLoading) return;
    this.isActionLoading = true;
    this.apiService.deleteResponseDraft(this.requestId, draftId).subscribe({
      next: (data) => {
        this.applyCard(data);
        this.isActionLoading = false;
      },
      error: () => {
        this.isActionLoading = false;
      }
    });
  }

  getDeliveryMethodLabel(method: string): string {
    return this.translateService.instant(`pages.viewInfoRequest.response.deliveryMethod.${method}`);
  }

  getNamejsSendingTypeLabel(namejsId: number | null | undefined, fallbackMethod: string): string {
    if (namejsId != null) {
      const key = `pages.viewInfoRequest.response.namejsSendingType.${namejsId}`;
      const v = this.translateService.instant(key);
      if (v !== key) return v;
    }
    return this.getDeliveryMethodLabel(fallbackMethod);
  }

  // "Nosūtīšanas notikumi Namejā" table shows only Namejs-polled externals.
  namejsExternalSendingEvents(events: ReadonlyArray<SendingEventItem> | null | undefined): ReadonlyArray<SendingEventItem> {
    return (events ?? []).filter((e) => e.source === 'NamejsExternal');
  }

  // "Nosūtīšanas notikumi BO" table + per-event detail cards: only LNAEP-operator-recorded
  // events (email send, portal send, mark-as-sent). These carry rich LNAEP fields
  // (email subject/body, notes) that don't exist on polled events.
  lnaepSendingEvents(events: ReadonlyArray<SendingEventItem> | null | undefined): ReadonlyArray<SendingEventItem> {
    return (events ?? []).filter((e) => e.source !== 'NamejsExternal');
  }

  // The file shown for an operator-recorded sending event is the immutable per-send snapshot
  // frozen when that event was sent (attachmentId/attachmentName) — NOT the parent
  // namejsReply/namejsForwarding `files`, which mirror the latest Namejs sync and rotate
  // whenever an operator re-fetches the document. Returns null when the event carries no
  // frozen snapshot, so the template hides the file row rather than falling back to a file
  // that may differ from what the client actually received. See LNA-1544.
  sendingEventFile(evt: SendingEventItem): { outerId: string; uploadFileName: string | null } | null {
    if (!evt.attachmentId) return null;
    return { outerId: evt.attachmentId, uploadFileName: evt.attachmentName ?? null };
  }

  // === Communication ===

  /**
   * Applied when the add/edit-client-message modal re-fetches the request on open (or after a
   * rejected submission). Updates the request in place — crucially WITHOUT toggling isLoading,
   * since that would unmount the conversation block (and the open modal with it). Keeps the
   * status badge and status-gated actions in sync with reality on a stale page.
   */
  onRequestRefreshed(data: InformationRequestCard): void {
    this.request = data;
    this.enrichStructuralUnitNames();
  }

  onMessageSaved(message: BackofficeInformationRequestCommunicationListItem | BackofficeInformationRequestCommunicationCard): void {
    this.isLoading = true;
    forkJoin([this.apiService.getIrCommunicationList(this.requestId), this.apiService.getInformationRequest(this.requestId)]).subscribe(
      ([conversation, data]) => {
        this.conversationList = conversation;
        this.applyCard(data);
        this.messageCount = this.conversationList?.totalCount ?? 0;
        this.unreadMessageCount = this.conversationList?.list.filter((msg) => !msg.isReadByBackoffice && msg.frontofficeText).length ?? 0;
        this.hasUnreadMessages = this.unreadMessageCount > 0;
        this.navItemsCounts = {
          infoRequestCommunication: this.messageCount,
          infoRequestProcessNotes: this.notesCount,
          infoRequestTasks: this.taskCount,
          infoRequestInvoice: this.invoiceTabCount,
          infoRequestDocuments: this.irDocumentsCount
        };
        this.isLoading = false;
      }
    );
  }

  checkUnreadMessages(): void {
    this.unreadMessageCount = this.unreadMessageCount - 1;
    this.hasUnreadMessages = this.unreadMessageCount > 0;
  }

  // === Processing Notes ===

  private refreshNotes(): void {
    this.apiService.getIrNotesByRequestId(this.requestId).subscribe((notes) => {
      this.notes = notes;
      this.notesCount = notes.length;
      this.navItemsCounts = {
        infoRequestCommunication: this.messageCount,
        infoRequestProcessNotes: this.notesCount,
        infoRequestTasks: this.taskCount,
        infoRequestInvoice: this.invoiceTabCount,
        infoRequestDocuments: this.irDocumentsCount
      };
    });
  }

  startAddNote(): void {
    this.newNoteText = '';
    this.isAddingNote = true;
  }

  cancelAddNote(): void {
    this.isAddingNote = false;
    this.newNoteText = '';
  }

  confirmAddNote(): void {
    if (this.isActionLoading || !this.newNoteText.trim()) return;
    this.isActionLoading = true;
    this.apiService
      .createIrNote({
        informationRequestId: this.requestId,
        text: this.newNoteText.trim()
      })
      .subscribe({
        next: (response) => {
          this.isActionLoading = false;
          this.newNoteId = response.id;
          this.isSubmittingNewNoteFiles = true;
        },
        error: () => {
          this.toastService.showRequestError();
          this.isActionLoading = false;
        }
      });
  }

  handleNewNoteFileUploads(uploads$: Observable<any>[]): void {
    if (uploads$.length === 0) {
      this.finishAddNote();
      return;
    }
    runUploads(uploads$).subscribe(results => {
      this.notifyUploadFailures(results);
      this.finishAddNote();
    });
  }

  private finishAddNote(): void {
    this.isSubmittingNewNoteFiles = false;
    this.isAddingNote = false;
    this.newNoteText = '';
    this.newNoteId = '';
    this.refreshNotes();
  }

  startEditNote(note: InformationRequestNoteListByInformationRequestIdItem): void {
    this.editingNoteId = note.id;
    this.editingNoteText = note.text;
  }

  cancelEditNote(): void {
    this.editingNoteId = null;
    this.editingNoteText = '';
  }

  confirmEditNote(): void {
    if (!this.editingNoteId || this.isActionLoading || !this.editingNoteText.trim()) return;
    // LNA-2050 #11: read before the save starts, which clears the component's pending changes.
    this.editingNoteFilesChanged = this.editNoteFileUpload?.hasPendingChanges() ?? false;
    this.isActionLoading = true;
    this.isSubmittingNoteFiles = true;
  }

  handleEditNoteFileUploads(uploads$: Observable<any>[]): void {
    const saveText$ = this.apiService.updateIrNote({
      id: this.editingNoteId!,
      text: this.editingNoteText.trim(),
      filesChanged: this.editingNoteFilesChanged
    });
    forkJoin([saveText$, ...isolateUploads(uploads$)]).subscribe({
      next: results => {
        this.notifyUploadFailures(results.slice(1));
        this.isActionLoading = false;
        this.editingNoteId = null;
        this.editingNoteText = '';
        this.isSubmittingNoteFiles = false;
        this.refreshNotes();
      },
      error: () => {
        this.toastService.showRequestError();
        this.isActionLoading = false;
        this.isSubmittingNoteFiles = false;
      }
    });
  }

  confirmDeleteNote(noteId: string): void {
    this.noteToDeleteId = noteId;
    this.isConfirmDeleteNoteOpen = true;
  }

  onDeleteNoteModalClose(): void {
    this.isConfirmDeleteNoteOpen = false;
    this.noteToDeleteId = null;
  }

  deleteNote(noteId: string): void {
    if (this.isActionLoading) return;
    this.isConfirmDeleteNoteOpen = false;
    this.isActionLoading = true;
    this.apiService.deleteIrNote(noteId).subscribe({
      next: () => {
        this.isActionLoading = false;
        this.refreshNotes();
      },
      error: () => {
        this.toastService.showRequestError();
        this.isActionLoading = false;
      }
    });
  }

  // === Tasks ===

  private computeMinTaskOrder(): void {
    const activeTasks = this.taskList.filter((t) => t.status === 'Assigned' || t.status === 'Accepted');
    if (activeTasks.length === 0) {
      this.minTaskExecutionOrder = 1;
    } else {
      this.minTaskExecutionOrder = Math.min(...activeTasks.map((t) => t.executionOrder));
    }
  }

  onTaskCreated(_task: InformationRequestTaskCard): void {
    this.refreshTaskList();
  }

  onTaskStatusChanged(): void {
    this.loadData();
  }

  onRequestUpdated(card: BackofficeInformationRequestCard): void {
    this.request = card;
  }

  onInvoiceChanged(): void {
    this.loadData();
  }

  private refreshTaskList(): void {
    this.apiService.getIrTaskList(this.requestId).subscribe((result) => {
      this.taskList = result.page.list;
      this.taskCount = this.countAcceptableTasks(this.taskList);
      this.computeMinTaskOrder();
      this.updateHasActiveTasks();
      this.navItemsCounts = {
        ...this.navItemsCounts,
        infoRequestTasks: this.taskCount
      };
    });
  }

  private countAcceptableTasks(tasks: any[]): number {
    return tasks.length;
  }

  private updateHasActiveTasks(): void {
    this.hasActiveTasks = this.taskList.some((t) => t.status === 'Waiting' || t.status === 'Assigned' || t.status === 'Accepted');
  }

  // === Documents tab ===

  // Re-extracting a document from Namejs overwrites the previous one in /documents, so the
  // document table only surfaces the latest signed file. Every extracted file that was already
  // sent survives as a sending-event attachment, so we merge those back into the matching Namejs
  // document row — both files then show and both stay non-deletable (LNA-1972).
  private refreshDocuments(): void {
    this.apiService.getIrDocuments(this.requestId).subscribe((documents) => {
      this.irDocuments = documents;
      this.irDocumentsCount = this.irDocuments.length;
      this.navItemsCounts = {
        ...this.navItemsCounts,
        infoRequestDocuments: this.irDocumentsCount
      };
    });
  }

  getDocumentRegisteredByDisplay(doc: InformationRequestDocumentItem): string {
    if (doc.isClientUploaded) return '-';
    const user = doc.registeredByUser;
    if (!user) return '-';
    const parts = [
      `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
      user.structuralUnitCodes?.length ? user.structuralUnitCodes.join(', ') : null,
      this.formatDatetime(doc.registeredOn)
    ].filter(Boolean);
    return parts.join('\n');
  }

  startAddDocument(): void {
    this.newDocumentDescription = '';
    this.newDocumentTouched = false;
    this.newDocumentFile = null;
    this.isAddingDocument = true;
  }

  cancelAddDocument(): void {
    this.isAddingDocument = false;
    this.newDocumentDescription = '';
    this.newDocumentTouched = false;
    this.newDocumentFile = null;
  }

  onNewDocumentFileAdded(event: NgxDropzoneChangeEvent): void {
    if (event.addedFiles.length > 0) {
      this.newDocumentFile = event.addedFiles[0];
    }
  }

  removeNewDocumentFile(): void {
    this.newDocumentFile = null;
  }

  confirmAddDocument(): void {
    this.newDocumentTouched = true;
    if (this.isActionLoading || !this.newDocumentFile) return;
    this.isActionLoading = true;
    this.apiService
      .uploadFile(this.newDocumentFile, this.fileTypeBoDocument, this.requestId, this.newDocumentDescription.trim() || '')
      .subscribe({
        next: () => {
          this.isActionLoading = false;
          this.isAddingDocument = false;
          this.newDocumentDescription = '';
          this.newDocumentTouched = false;
          this.newDocumentFile = null;
          this.refreshDocuments();
        },
        error: () => {
          this.toastService.showRequestError();
          this.isActionLoading = false;
        }
      });
  }

  startEditDocument(doc: InformationRequestDocumentItem): void {
    this.editingDocumentOuterId = doc.files[0]?.outerId ?? null;
    this.editingDocumentDescription =
      doc.editableDescription === undefined ? (doc.description ?? '') : (doc.editableDescription ?? '');
    this.editingDocumentMaxLength =
      doc.objectType === 'InformationRequestOtherResponseFile'
        ? MAX_OTHER_RESPONSE_DESCRIPTION_LENGTH
        : this.MAX_DOCUMENT_DESCRIPTION_LENGTH;
    this.editDocumentTouched = false;
  }

  cancelEditDocument(): void {
    this.editingDocumentOuterId = null;
    this.editingDocumentDescription = '';
    this.editDocumentTouched = false;
  }

  confirmEditDocument(): void {
    this.editDocumentTouched = true;
    if (!this.editingDocumentOuterId || this.isActionLoading || !this.editingDocumentDescription.trim()) return;
    this.isActionLoading = true;
    this.apiService
      .updateIrDocumentDescription(this.requestId, this.editingDocumentOuterId, this.editingDocumentDescription.trim())
      .subscribe({
        next: () => {
          this.isActionLoading = false;
          this.editingDocumentOuterId = null;
          this.editingDocumentDescription = '';
          this.editDocumentTouched = false;
          this.refreshDocuments();
        },
        error: (err) => {
          this.toastService.showRequestError(err);
          this.isActionLoading = false;
        }
      });
  }

  getDocumentDescription(doc: InformationRequestDocumentItem): string {
    const text = this.resolveDocumentText(doc);
    // Prefix the section the file comes from ("Kalkulācija — ...") so it is identifiable in the
    // shared table, mirroring how invoices/communication already read (LNA-1974). Those two and
    // Namejs files already carry a descriptive prefix from the BE, so they get no extra label.
    const sectionKey = this.getDocumentSourceKey(doc.objectType);
    if (!sectionKey) return text || '-';
    const section = this.translateService.instant(`pages.viewInfoRequest.documents.source.${sectionKey}`);
    return text ? `${section} — ${text}` : section;
  }

  private resolveDocumentText(doc: InformationRequestDocumentItem): string {
    if (doc.description) return doc.description;
    // The BE resolves descriptions for all groups except response drafts (it intentionally
    // does not surface the draft body). Keep that single client-side fallback.
    if (doc.objectType === FILE_OBJECT_TYPE_INFO_REQUEST_RESPONSE_DRAFT) {
      const draft = (this.request?.responseDrafts || []).find((d) =>
        (d.files || []).some((f) => doc.files.some((df) => df.outerId === f.outerId))
      );
      return draft?.text || '';
    }
    return '';
  }

  private getDocumentSourceKey(objectType: InformationRequestDocumentItem['objectType']): string | null {
    switch (objectType) {
      case FILE_OBJECT_TYPE_INFO_REQUEST_DOCUMENT:
      case FILE_OBJECT_TYPE_INFO_REQUEST_AUTHORIZATION:
      case FILE_OBJECT_TYPE_INFO_REQUEST_DISCOUNT:
      case FILE_OBJECT_TYPE_INFO_REQUEST_BO_DOCUMENT:
        return 'request';
      case FILE_OBJECT_TYPE_INFO_REQUEST_CALCULATION:
        return 'calculation';
      case FILE_OBJECT_TYPE_INFO_REQUEST_OTHER_RESPONSE:
        return 'otherResponse';
      case FILE_OBJECT_TYPE_INFO_REQUEST_RESPONSE_DRAFT:
        return 'responseDraft';
      case FILE_OBJECT_TYPE_INFO_REQUEST_NOTE:
        return 'processNotes';
      default:
        // Invoice / Communication already come prefixed from the BE; Namejs signed docs carry
        // their own label. Leave those untouched to avoid a doubled prefix.
        return null;
    }
  }

  confirmDeleteDocument(outerId: string): void {
    this.documentToDeleteOuterId = outerId;
    this.isConfirmDeleteDocumentOpen = true;
  }

  onDeleteDocumentModalClose(): void {
    this.isConfirmDeleteDocumentOpen = false;
    this.documentToDeleteOuterId = null;
  }

  deleteDocument(outerId: string): void {
    if (this.isActionLoading) return;
    this.isConfirmDeleteDocumentOpen = false;
    this.isActionLoading = true;
    this.apiService.deleteFile(outerId).subscribe({
      next: () => {
        this.isActionLoading = false;
        this.refreshDocuments();
      },
      error: () => {
        this.toastService.showRequestError();
        this.isActionLoading = false;
      }
    });
  }

  downloadDocumentFile(file: InformationRequestDocumentFile): void {
    this.apiService.downloadFile(file.outerId).subscribe({
      next: (data) => {
        const blob = new Blob([data], { type: 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.uploadFileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: () => this.toastService.showRequestError()
    });
  }
}
